import { NotificationType } from '~/app/enums/NotificationId';
import { NotificationLevel } from '~/app/enums/NotificationLevel';
import { Configuration } from '~/app/models/Configuration';
import { SystemNotification } from '~/app/models/Notification';
import { TherapyJobV2 } from '~/app/models/TherapyJobV2';
import { getDuration } from '~/app/pipes/TimeAgo';
import { NotificationsStore } from '~/app/store/notification.store';
import { flowRateDifference } from '../flowRateDifference';
import { volumeDifference } from '../VolumeDifference';
import { createNotification, NotificationBuilder } from './NotificationBuilder';
import { notificationId } from './notificationId';
import { noVolumeProvided } from './NoVolumeProvided';
import { therapyJobUpdated } from './TherapyJobUpdated';

type Args = [
  NotificationsStore,
  Partial<TherapyJobV2>,
  boolean,
  boolean?,
  Configuration?,
  string?,
  SystemNotification?
];

type NotificationFunction = (...args: Args) => void;

/** Pending confirmation escalates Info → Warning at `escalatesAt`, switched by the ticker without a server event. */
export const serverLevel = (notification: SystemNotification): NotificationLevel => {
  const escalated =
    NotificationType[notification.type] === NotificationType.PENDING_CONFIRMATION &&
    Number.isFinite(notification.escalatesAt) &&
    Date.now() >= notification.escalatesAt;

  return escalated ? NotificationLevel.WARNING : NotificationLevel[notification.severity];
};

/**
 * Renders a notification the server decided, with its severity and raise time: shown exactly while the job's
 * `notifications` contain it (DEVICE_DISCONNECTED: while the web UI derives it from the job).
 * `params` adds the translation parameters the web UI derives from the job.
 */
const serverRow =
  (
    type: NotificationType,
    params: (
      builder: NotificationBuilder,
      tj: Partial<TherapyJobV2>,
      locale: string,
      systemNotification: SystemNotification
    ) => NotificationBuilder = (builder) => builder
  ): NotificationFunction =>
  (...args: Args) => {
    const [store, tj, notified, notify, _config, locale, systemNotification] = args;
    if (!tj) {
      return;
    }

    const builder = createNotification(tj)
      .type(type)
      .showAlarmCenter(type !== NotificationType.PENDING_CONFIRMATION);
    if (systemNotification) {
      builder.level(serverLevel(systemNotification)).timestamp(systemNotification.since);
    }

    params(builder, tj, locale, systemNotification).buildAndPatch(notify, store, notified);
  };

const withDeviceId = (builder: NotificationBuilder, tj: Partial<TherapyJobV2>) =>
  builder.addTranslationParam('deviceId', tj.monitorDeviceId);

/** The web UI computes the displayed difference of a server deviation notification from the progress of the job. */
const withDifference =
  (translationParam: string, difference: (tj: Partial<TherapyJobV2>) => number | null) =>
  (builder: NotificationBuilder, tj: Partial<TherapyJobV2>) =>
    builder.addTranslationParam(translationParam, difference(tj));

/** Every notification the server decides; rendered only from the job's set, never calculated by the web UI. */
const serverNotifications: Partial<Record<NotificationType, NotificationFunction>> = {
  [NotificationType.THERAPY_JOB_ON_HOLD]: serverRow(NotificationType.THERAPY_JOB_ON_HOLD),
  [NotificationType.THERAPY_JOB_CANCELLED]: serverRow(NotificationType.THERAPY_JOB_CANCELLED),
  [NotificationType.THERAPY_JOB_STOPPED]: serverRow(NotificationType.THERAPY_JOB_STOPPED),
  [NotificationType.THERAPY_JOB_ENTERED_IN_ERROR]: serverRow(NotificationType.THERAPY_JOB_ENTERED_IN_ERROR),
  [NotificationType.PATIENT_DISCHARGED]: serverRow(NotificationType.PATIENT_DISCHARGED),
  [NotificationType.PATIENT_NOT_ADMITTED]: serverRow(NotificationType.PATIENT_NOT_ADMITTED),
  [NotificationType.DRIP_CHAMBER_DETACHED]: serverRow(NotificationType.DRIP_CHAMBER_DETACHED, withDeviceId),
  [NotificationType.THERAPY_JOB_UNPAIRED_FROM_MD]: serverRow(NotificationType.THERAPY_JOB_UNPAIRED_FROM_MD),
  // on the card only, not in the notification center
  [NotificationType.PENDING_CONFIRMATION]: serverRow(
    NotificationType.PENDING_CONFIRMATION,
    (builder, tj, locale, systemNotification) =>
      withDeviceId(builder, tj).addTranslationParam('timeAgo', getDuration(systemNotification?.since, locale))
  ),
  [NotificationType.FLOW_RATE_TOO_FAST]: serverRow(
    NotificationType.FLOW_RATE_TOO_FAST,
    withDifference('flowRate', flowRateDifference)
  ),
  [NotificationType.FLOW_RATE_TOO_SLOW]: serverRow(
    NotificationType.FLOW_RATE_TOO_SLOW,
    withDifference('flowRate', flowRateDifference)
  ),
  [NotificationType.FLOW_RATE_UNKNOWN]: serverRow(NotificationType.FLOW_RATE_UNKNOWN),
  [NotificationType.VOLUME_TOO_HIGH]: serverRow(
    NotificationType.VOLUME_TOO_HIGH,
    withDifference('volume', volumeDifference)
  ),
  [NotificationType.VOLUME_UNKNOWN]: serverRow(NotificationType.VOLUME_UNKNOWN),
  [NotificationType.THERAPY_JOB_MARK_FOR_DELETION]: serverRow(NotificationType.THERAPY_JOB_MARK_FOR_DELETION),
  [NotificationType.THERAPY_JOB_ABORT_TRIGGERED]: serverRow(NotificationType.THERAPY_JOB_ABORT_TRIGGERED, withDeviceId),
  [NotificationType.INFUSION_IN_ERROR]: serverRow(NotificationType.INFUSION_IN_ERROR),
  [NotificationType.DEVICE_DISCONNECTED]: serverRow(NotificationType.DEVICE_DISCONNECTED)
};

/** The only notifications the web UI calculates; neither is shown in the notification center. */
const webUiNotifications: Partial<Record<NotificationType, NotificationFunction>> = {
  [NotificationType.NO_VOLUME_PROVIDED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    createNotification(tj)
      .type(NotificationType.NO_VOLUME_PROVIDED)
      .showAlarmCenter(false)
      .buildAndPatch(notify || noVolumeProvided(tj), store, notified);
  },
  [NotificationType.THERAPY_JOB_UPDATED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_UPDATED)
      .showAlarmCenter(false)
      .buildAndPatch(notify || therapyJobUpdated(store, tj), store, notified);
  }
};

const notificationFunctions = { ...serverNotifications, ...webUiNotifications };

/** Types the web UI renders from the server's decision (the job's `notifications`, DEVICE_DISCONNECTED derived). */
export const isServerNotification = (type: NotificationType): boolean => !!serverNotifications[type];

/** Types the web UI calculates itself. */
export const isWebUiNotification = (type: NotificationType): boolean => !!webUiNotifications[type];

/*
  web UI notifications are built checking their conditions to show or hide them
*/
export const checkAllNotificationConditionsPerJob = (
  therapyJob: Partial<TherapyJobV2>,
  store,
  configuration,
  locale?: string
) => {
  if (!therapyJob) {
    console.warn('No therapy job provided to check notifications conditions.');
    return;
  }
  Object.keys(webUiNotifications).forEach((key: NotificationType) => {
    const notified = !!store.entityMap()?.[notificationId(key, therapyJob.id)];
    const notifyAnyways = false;

    webUiNotifications[key](store, therapyJob, notified, notifyAnyways, configuration, locale);
  });
};

/*
  build one type of notification for a therapy job
*/
export const buildNotification = (
  notificationType: NotificationType,
  job: Partial<TherapyJobV2>,
  store,
  configuration,
  notifyAnyways = false,
  locale?: string,
  systemNotification?: SystemNotification
) => {
  if (!job?.id || !notificationFunctions[notificationType]) {
    return;
  }

  const notified = !!store.entityMap()?.[notificationId(notificationType, job.id)];
  notificationFunctions[notificationType](
    store,
    job,
    notified,
    notifyAnyways,
    configuration,
    locale,
    systemNotification
  );
};
